import React from 'react';
import { motion } from 'framer-motion';
import '../styles/pages/TermsOfService.css';

const TermsOfService = () => {
  return (
    <div className="terms-of-service">
      {/* Hero Section */}
      <section className="terms-hero">
        <div className="terms-container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="terms-hero-content"
          >
            <div className="hero-badge">
              📋 Terms of Service
            </div>
            
            <h1 className="hero-title">
              Terms of <span className="gradient-text">Service</span>
            </h1>
            
            <p className="hero-subtitle">
              Last Updated: January 1, 2024
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="terms-content">
        <div className="terms-container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="terms-text"
          >
            <div className="terms-section">
              <p className="terms-intro">
                Welcome to RKube Inc ("we," "us," or "our"). By accessing or using our website, services, or any related content (collectively, the "Services"), you agree to comply with and be bound by these Terms of Use. If you do not agree with these terms, please refrain from using our Services.
              </p>
            </div>

            <div className="terms-section">
              <h2>1. Use of Services</h2>
              
              <h3>1.1 Eligibility</h3>
              <p>You must be at least 18 years old to use our Services. By using the Services, you affirm that you are at least 18 years old and have the legal authority to enter into these terms on behalf of yourself or your organization.</p>

              <h3>1.2 User Account</h3>
              <p>Some features of our Services may require you to create an account. You are responsible for:</p>
              <ul>
                <li>Maintaining the confidentiality of your account information</li>
                <li>All activities that occur under your account</li>
                <li>Notifying us immediately of any unauthorized use of your account</li>
                <li>Ensuring that your account information is accurate and up-to-date</li>
              </ul>

              <h3>1.3 Acceptable Use</h3>
              <p>You agree not to use the Services for any unlawful or prohibited purpose, including but not limited to:</p>
              <ul>
                <li>Violating any applicable laws or regulations</li>
                <li>Disrupting the functionality of the Services</li>
                <li>Attempting to gain unauthorized access to any part of the Services</li>
                <li>Interfering with other users' use and enjoyment of the Services</li>
                <li>Transmitting viruses, malware, or other harmful code</li>
              </ul>
            </div>

            <div className="terms-section">
              <h2>2. Content</h2>

              <h3>2.1 User-Generated Content</h3>
              <p>By submitting content to our Services, you:</p>
              <ul>
                <li>Grant us a non-exclusive, royalty-free, worldwide, perpetual license to use, display, reproduce, and distribute the content</li>
                <li>Represent that you have the right to grant such license</li>
                <li>Acknowledge that we may use your content to provide and improve our Services</li>
              </ul>

              <h3>2.2 Prohibited Content</h3>
              <p>You may not submit content that:</p>
              <ul>
                <li>Is illegal, offensive, defamatory, or violates the rights of others</li>
                <li>Contains confidential information without proper authorization</li>
                <li>Infringes on intellectual property rights</li>
                <li>Contains malicious software or code</li>
                <li>Is intended to deceive or mislead</li>
              </ul>
            </div>

            <div className="terms-section">
              <h2>3. Intellectual Property</h2>

              <h3>3.1 Ownership</h3>
              <p>All content and materials available on the Services, including but not limited to text, graphics, logos, software, and images, are the property of RKube Inc or its licensors and are protected by copyright, trademark, and other intellectual property laws.</p>

              <h3>3.2 License</h3>
              <p>You are granted a limited, non-exclusive, non-transferable license to access and use the Services for legitimate business purposes in accordance with these terms. This license does not include the right to:</p>
              <ul>
                <li>Reproduce, distribute, or publicly display our content</li>
                <li>Create derivative works based on our content</li>
                <li>Reverse engineer any software used in our Services</li>
                <li>Remove or modify any copyright or proprietary notices</li>
              </ul>
            </div>

            <div className="terms-section">
              <h2>4. Professional Services</h2>

              <h3>4.1 Service Delivery</h3>
              <p>Our business process outsourcing, financial services, and technology solutions are provided according to the terms of specific service agreements. These Terms of Use supplement but do not replace such agreements.</p>

              <h3>4.2 Professional Standards</h3>
              <p>We maintain professional standards in all our services, including:</p>
              <ul>
                <li>Compliance with applicable professional and regulatory standards</li>
                <li>Confidentiality and data security measures</li>
                <li>Quality assurance processes</li>
                <li>Timely service delivery</li>
              </ul>
            </div>

            <div className="terms-section">
              <h2>5. Privacy</h2>

              <h3>5.1 Privacy Policy</h3>
              <p>Your use of the Services is also governed by our Privacy Policy. By using the Services, you consent to the practices described in the Privacy Policy. Please review our Privacy Policy to understand how we collect, use, and protect your information.</p>
            </div>

            <div className="terms-section">
              <h2>6. Termination</h2>

              <h3>6.1 Termination by Us</h3>
              <p>We reserve the right to terminate or suspend your access to the Services at our sole discretion, without notice or liability, for any reason including but not limited to:</p>
              <ul>
                <li>Violation of these Terms of Use</li>
                <li>Suspected fraudulent, abusive, or illegal activity</li>
                <li>Extended periods of inactivity</li>
                <li>Technical or security issues</li>
              </ul>

              <h3>6.2 Termination by You</h3>
              <p>You may stop using the Services at any time. If you have active service agreements, termination will be subject to the terms of those specific agreements.</p>

              <h3>6.3 Effect of Termination</h3>
              <p>Upon termination, your right to use the Services will immediately cease. Data retention and return will be handled according to our Privacy Policy and any applicable service agreements.</p>
            </div>

            <div className="terms-section">
              <h2>7. Disclaimers</h2>

              <h3>7.1 No Warranty</h3>
              <p>The Services are provided "as is" and "as available" without warranties of any kind, either express or implied, including but not limited to warranties of merchantability, fitness for a particular purpose, or non-infringement.</p>

              <h3>7.2 Limitation of Liability</h3>
              <p>To the fullest extent permitted by law, we shall not be liable for any direct, indirect, incidental, special, consequential, or punitive damages arising out of or in connection with your use of the Services, including but not limited to:</p>
              <ul>
                <li>Loss of profits, data, or business opportunities</li>
                <li>Service interruptions or delays</li>
                <li>Third-party actions or content</li>
                <li>Security breaches beyond our reasonable control</li>
              </ul>
            </div>

            <div className="terms-section">
              <h2>8. Indemnification</h2>
              <p>You agree to indemnify, defend, and hold harmless RKube Inc, its officers, directors, employees, and agents from any claims, damages, obligations, losses, liabilities, costs, or debt arising from:</p>
              <ul>
                <li>Your use of the Services</li>
                <li>Your violation of these Terms of Use</li>
                <li>Your violation of any third-party rights</li>
                <li>Any content you submit or transmit through the Services</li>
              </ul>
            </div>

            <div className="terms-section">
              <h2>9. Governing Law</h2>
              <p>These Terms of Use are governed by and construed in accordance with the laws of the State of North Carolina, United States, without regard to its conflict of law principles. Any legal action or proceeding relating to these Terms shall be brought exclusively in the courts of North Carolina.</p>
            </div>

            <div className="terms-section">
              <h2>10. Changes to Terms</h2>
              <p>We reserve the right to update or modify these Terms of Use at any time without prior notice. Your continued use of the Services after any changes signifies your acceptance of the revised terms. We encourage you to review these terms periodically for any updates.</p>
            </div>

            <div className="terms-section">
              <h2>11. Severability</h2>
              <p>If any provision of these Terms of Use is found to be unenforceable or invalid, such provision will be changed and interpreted to accomplish the objectives of such provision to the greatest extent possible under applicable law, and the remaining provisions will continue in full force and effect.</p>
            </div>

            <div className="terms-section">
              <h2>12. Contact Us</h2>
              <p>If you have any questions about these Terms of Use, please contact us at:</p>
              <div className="contact-info">
                <p><strong>Email:</strong> support@rkubeinc.com</p>
                <p><strong>Phone:</strong> (984) 234-7007</p>
                <p><strong>Address:</strong> 4030 Wake Forest Road, STE 349<br />Raleigh, NC 27609, USA</p>
              </div>
            </div>

            <div className="terms-section terms-closing">
              <p className="closing-statement">
                Thank you for using RKube Inc. We appreciate your cooperation and hope you find our comprehensive business process outsourcing, financial services, and technology solutions valuable for your business growth.
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default TermsOfService; 